'use client';

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Column } from '../types';

// 节点输出的 Schema 信息
export interface NodeOutputSchema {
  nodeId: string;
  nodeType: string;
  nodeName: string;
  columns: Column[];
  sourceDataFileId?: string; // 如果是从数据文件加载的，记录源文件ID
}

// Schema 上下文类型
interface SchemaContextType {
  // 所有可用的 Schema（来自数据文件和上游节点）
  availableSchemas: NodeOutputSchema[];
  
  // 注册一个节点的输出 Schema
  registerNodeSchema: (schema: NodeOutputSchema) => void;
  
  // 移除一个节点的 Schema
  unregisterNodeSchema: (nodeId: string) => void;
  
  // 更新节点的 Schema
  updateNodeSchema: (nodeId: string, updates: Partial<NodeOutputSchema>) => void;
  
  // 获取特定节点的 Schema
  getNodeSchema: (nodeId: string) => NodeOutputSchema | undefined;
  
  // 获取所有可用的列（用于下拉选择）
  getAllAvailableColumns: () => Array<{ column: Column; source: NodeOutputSchema }>;
  
  // 获取特定源的所有列
  getColumnsBySource: (sourceId: string) => Column[];
  
  // 获取指定节点的输入节点的 Schema（用于 Join 等多输入节点）
  getInputSchemasForNode: (nodeId: string, inputIndex?: number) => NodeOutputSchema | NodeOutputSchema[] | undefined;
  
  // 从数据文件加载 Schema
  loadSchemaFromDataFile: (fileId: string, fileName: string, columns: Column[]) => void;
  
  // 清除所有 Schema
  clearAllSchemas: () => void;
}

const SchemaContext = createContext<SchemaContextType | undefined>(undefined);

export function SchemaProvider({ children }: { children: React.ReactNode }) {
  const [availableSchemas, setAvailableSchemas] = useState<NodeOutputSchema[]>([]);

  const registerNodeSchema = useCallback((schema: NodeOutputSchema) => {
    setAvailableSchemas(prev => {
      const existing = prev.find(s => s.nodeId === schema.nodeId);
      if (existing) {
        return prev.map(s => s.nodeId === schema.nodeId ? { ...s, ...schema } : s);
      }
      return [...prev, schema];
    });
  }, []);

  const unregisterNodeSchema = useCallback((nodeId: string) => {
    setAvailableSchemas(prev => prev.filter(s => s.nodeId !== nodeId));
  }, []);

  const updateNodeSchema = useCallback((nodeId: string, updates: Partial<NodeOutputSchema>) => {
    setAvailableSchemas(prev =>
      prev.map(s => s.nodeId === nodeId ? { ...s, ...updates } : s)
    );
  }, []);

  const getNodeSchema = useCallback((nodeId: string) => {
    return availableSchemas.find(s => s.nodeId === nodeId);
  }, [availableSchemas]);

  const getAllAvailableColumns = useCallback(() => {
    const columns: Array<{ column: Column; source: NodeOutputSchema }> = [];
    availableSchemas.forEach(schema => {
      schema.columns.forEach(column => {
        columns.push({ column, source: schema });
      });
    });
    return columns;
  }, [availableSchemas]);

  const getColumnsBySource = useCallback((sourceId: string) => {
    const schema = availableSchemas.find(s => s.nodeId === sourceId);
    return schema?.columns || [];
  }, [availableSchemas]);

  // 获取指定节点的输入节点的 Schema
  // 需要配合 workflowStore 使用，这里提供一个基础实现
  const getInputSchemasForNode = useCallback((_nodeId: string, _inputIndex?: number): NodeOutputSchema | NodeOutputSchema[] | undefined => {
    // 这个方法需要在组件中使用，配合 workflowStore 获取 edges
    // 返回指定索引的输入 schema，或所有输入 schemas
    return undefined;
  }, []);

  const loadSchemaFromDataFile = useCallback((fileId: string, fileName: string, columns: Column[]) => {
    const schema: NodeOutputSchema = {
      nodeId: `datafile-${fileId}`,
      nodeType: 'data_source',
      nodeName: fileName,
      columns,
      sourceDataFileId: fileId,
    };
    
    setAvailableSchemas(prev => {
      const filtered = prev.filter(s => s.sourceDataFileId !== fileId);
      return [...filtered, schema];
    });
  }, []);

  const clearAllSchemas = useCallback(() => {
    setAvailableSchemas([]);
  }, []);

  const value = useMemo(() => ({
    availableSchemas,
    registerNodeSchema,
    unregisterNodeSchema,
    updateNodeSchema,
    getNodeSchema,
    getAllAvailableColumns,
    getColumnsBySource,
    getInputSchemasForNode,
    loadSchemaFromDataFile,
    clearAllSchemas,
  }), [
    availableSchemas,
    registerNodeSchema,
    unregisterNodeSchema,
    updateNodeSchema,
    getNodeSchema,
    getAllAvailableColumns,
    getColumnsBySource,
    getInputSchemasForNode,
    loadSchemaFromDataFile,
    clearAllSchemas,
  ]);

  return (
    <SchemaContext.Provider value={value}>
      {children}
    </SchemaContext.Provider>
  );
}

export function useSchema() {
  const context = useContext(SchemaContext);
  if (!context) {
    throw new Error('useSchema must be used within a SchemaProvider');
  }
  return context;
}

// Hook for node-specific schema operations
export function useNodeSchema(nodeId: string) {
  const schemaContext = useSchema();
  
  return {
    schema: schemaContext.getNodeSchema(nodeId),
    register: (schema: Omit<NodeOutputSchema, 'nodeId'>) => {
      schemaContext.registerNodeSchema({ ...schema, nodeId });
    },
    unregister: () => {
      schemaContext.unregisterNodeSchema(nodeId);
    },
    update: (updates: Partial<NodeOutputSchema>) => {
      schemaContext.updateNodeSchema(nodeId, updates);
    },
  };
}
